import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '@/lib/auth-context';
import { useProfile } from '@/lib/profile-context';
import { noraAPI, sellersAPI, meetingAgentAPI } from '@/lib/api';
import { toast } from 'sonner';
import './book-nora.css';

const AGENDA_OPTS = [
  'Account health report walkthrough',
  'Connect Seller Central to Protego',
  'How to read reports and signals',
  'Plan and billing',
  'Settings and configuration',
  'Something else',
];

const AGENDA_HELP = {
  'Account health report walkthrough':
    'Most common. Nora generates a fresh audit before the session and reads it with you.',
  'Connect Seller Central to Protego':
    'Nora walks the SP-API authorization step by step on screen.',
  'How to read reports and signals':
    'Slope, Drift, Decay, Cluster, Divergence, Collapse — what each reading means.',
  'Plan and billing': 'Current plan, invoices, and what each tier covers.',
  'Settings and configuration':
    'Alert thresholds, notification routing, marketplace coverage.',
  'Something else': 'Describe it below so Nora can prepare.',
};

function NoraButton({ variant = 'primary', size = 'md', children, disabled, style, ...rest }) {
  const sizes = {
    sm: { height: 32, fontSize: 13, padding: '0 16px' },
    md: { height: 40, fontSize: 14, padding: '0 20px' },
    lg: { height: 48, fontSize: 16, padding: '0 24px' },
  };
  const variants = {
    primary: { background: 'var(--cyan)', color: 'var(--text-on-cyan)', border: 'none' },
    secondary: { background: 'transparent', color: 'var(--white)', border: '1px solid var(--border-strong)' },
    ghost: { background: 'transparent', color: 'var(--text-secondary)', border: 'none' },
  };
  return (
    <button
      type="button"
      disabled={disabled}
      style={{
        ...sizes[size],
        ...variants[variant],
        borderRadius: 'var(--r-md)',
        fontFamily: 'var(--font-body)',
        fontWeight: 500,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.45 : 1,
        transition: 'all var(--dur-micro) var(--ease)',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        ...style,
      }}
      {...rest}
    >
      {children}
    </button>
  );
}

function StatusBadge({ tone = 'info', children }) {
  const colors = {
    success: 'var(--success)',
    info: 'var(--info)',
    warning: 'var(--warning)',
    error: 'var(--error)',
  };
  const c = colors[tone] || colors.info;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        height: 24,
        padding: '0 10px',
        borderRadius: 'var(--r-full)',
        fontFamily: 'var(--font-body)',
        fontWeight: 500,
        fontSize: 13,
        letterSpacing: '.02em',
        color: c,
        border: `1px solid ${c}`,
        background: `color-mix(in srgb, ${c} 12%, transparent)`,
      }}
    >
      {children}
    </span>
  );
}

function fieldLabel(style) {
  return { fontSize: 13, fontWeight: 500, marginBottom: 8, ...style };
}

function selectStyle() {
  return {
    width: '100%',
    height: 44,
    appearance: 'none',
    background: 'var(--surface-2)',
    border: '1px solid var(--border-subtle)',
    borderRadius: 10,
    color: 'var(--text-primary)',
    fontFamily: 'var(--font-body)',
    fontSize: 14,
    padding: '0 40px 0 14px',
  };
}

function inputStyle() {
  return {
    width: '100%',
    height: 44,
    background: 'var(--surface-2)',
    border: '1px solid var(--border-subtle)',
    borderRadius: 10,
    color: 'var(--text-primary)',
    fontFamily: 'var(--font-body)',
    fontSize: 14,
    padding: '0 14px',
  };
}

function LiveDot() {
  return <span className="nora-live-dot" />;
}

function NoraWave({ speaking }) {
  return (
    <div style={{ display: 'flex', gap: 3, alignItems: 'center', height: 30, flex: 1, justifyContent: 'center' }}>
      {Array.from({ length: 18 }, (_, i) => (
        <span
          key={i}
          style={{
            width: 3,
            height: 6 + ((i * 7) % 17),
            background: '#00D4FF',
            borderRadius: 2,
            animation: `nora-wave ${(1 + (i % 5) * 0.16).toFixed(2)}s ease-in-out ${(i * 0.07).toFixed(2)}s infinite`,
            animationPlayState: speaking ? 'running' : 'paused',
            opacity: speaking ? 1 : 0.3,
          }}
        />
      ))}
    </div>
  );
}

const NORA_VOICE_KEY = 'protego_nora_voice_uri';

function listNoraVoices() {
  if (typeof window === 'undefined' || !window.speechSynthesis) return [];
  const voices = window.speechSynthesis.getVoices() || [];
  const en = voices.filter((v) => /^en(-|_)/i.test(v.lang) || /english/i.test(v.name));
  const list = (en.length ? en : voices).slice().sort((a, b) => a.name.localeCompare(b.name));
  return list;
}

function pickNoraVoice(preferredUri) {
  const voices = listNoraVoices();
  if (!voices.length) return null;
  if (preferredUri) {
    const match = voices.find((v) => v.voiceURI === preferredUri);
    if (match) return match;
  }
  const preferredNames = /samantha|karen|moira|victoria|google us english|microsoft aria|microsoft jenny|female/i;
  return voices.find((v) => preferredNames.test(v.name))
    || voices.find((v) => /en-US/i.test(v.lang))
    || voices[0]
    || null;
}

function stopNoraSpeech() {
  if (typeof window !== 'undefined' && window.speechSynthesis) {
    window.speechSynthesis.cancel();
  }
}

/** Reveal length for a SpeechSynthesis boundary event (word/sentence). */
function boundaryRevealLen(text, event) {
  const idx = Math.max(0, event?.charIndex || 0);
  const len = typeof event?.charLength === 'number' && event.charLength > 0
    ? event.charLength
    : 1;
  let end = Math.min(text.length, idx + len);
  while (end < text.length && /\s/.test(text[end])) end += 1;
  return end;
}

/**
 * Speak a line. Calls onProgress(charCount) as words are spoken (boundary),
 * with a timed fallback when the browser does not fire boundaries (e.g. Safari).
 */
function speakNoraLine(text, { onStart, onEnd, onProgress, voiceUri } = {}) {
  if (typeof window === 'undefined' || !window.speechSynthesis || !text) {
    onProgress?.(text?.length || 0);
    onEnd?.();
    return null;
  }
  stopNoraSpeech();
  const utterance = new SpeechSynthesisUtterance(text);
  utterance.rate = 1.02;
  utterance.pitch = 1.05;
  utterance.volume = 1;
  const voice = pickNoraVoice(voiceUri);
  if (voice) utterance.voice = voice;

  let finished = false;
  let usedBoundary = false;
  let revealed = 0;
  let fallbackTimer = null;

  const pushProgress = (n) => {
    const next = Math.min(text.length, Math.max(revealed, n));
    if (next === revealed) return;
    revealed = next;
    onProgress?.(revealed);
  };

  const clearFallback = () => {
    if (fallbackTimer) {
      clearInterval(fallbackTimer);
      fallbackTimer = null;
    }
  };

  const finish = () => {
    if (finished) return;
    finished = true;
    clearFallback();
    pushProgress(text.length);
    onEnd?.();
  };

  // ~16 chars/sec ≈ natural speech at rate ~1; used when boundary events are missing.
  const startFallbackTicker = () => {
    if (fallbackTimer || finished) return;
    const charsPerTick = Math.max(1, Math.round(utterance.rate * 0.65));
    fallbackTimer = setInterval(() => {
      if (finished) {
        clearFallback();
        return;
      }
      if (usedBoundary) return;
      pushProgress(revealed + charsPerTick);
      if (revealed >= text.length) clearFallback();
    }, 40);
  };

  utterance.onstart = () => {
    onStart?.();
    // Give boundary events a moment; if none arrive, type in sync with speech.
    setTimeout(() => {
      if (!finished && !usedBoundary) startFallbackTicker();
    }, 350);
  };
  utterance.onboundary = (event) => {
    if (event.name !== 'word' && event.name !== 'sentence') return;
    usedBoundary = true;
    clearFallback();
    pushProgress(boundaryRevealLen(text, event));
  };
  utterance.onend = finish;
  utterance.onerror = finish;

  const start = () => window.speechSynthesis.speak(utterance);
  if ((window.speechSynthesis.getVoices() || []).length === 0) {
    window.speechSynthesis.addEventListener('voiceschanged', start, { once: true });
    setTimeout(start, 250);
  } else {
    start();
  }
  return utterance;
}

/** Typewriter without speech (muted / unsupported). */
function typeNoraLine(text, { onProgress, onEnd, msPerChar = 22 } = {}) {
  if (!text) {
    onEnd?.();
    return () => {};
  }
  let i = 0;
  let stopped = false;
  const timer = setInterval(() => {
    if (stopped) return;
    i = Math.min(text.length, i + 1);
    onProgress?.(i);
    if (i >= text.length) {
      clearInterval(timer);
      onEnd?.();
    }
  }, msPerChar);
  return () => {
    stopped = true;
    clearInterval(timer);
  };
}

function getSpeechRecognitionCtor() {
  if (typeof window === 'undefined') return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

function sellerLabel(account, profile) {
  const name = account?.accountName || account?.name || 'Amazon account';
  const market = profile?.countryCode
    ? `amazon.${String(profile.countryCode).toLowerCase() === 'us' ? 'com' : String(profile.countryCode).toLowerCase()} (${profile.countryCode})`
    : profile?.name || 'profile';
  return `${name} · ${market}`;
}

export default function BookNoraPage() {
  const { user } = useAuth();
  const { accounts, currentAccount, currentProfile, hasSelection, accountId, profileId } = useProfile();

  const sellerOptions = useMemo(() => {
    const opts = [];
    (accounts || []).forEach((a) => {
      const label = a.accountName || a.name || `Account ${a.id}`;
      if (currentAccount && String(a.id) === String(currentAccount.id) && currentProfile) {
        opts.push(sellerLabel(a, currentProfile));
      } else {
        opts.push(`${label}${a.marketplace ? ` · ${a.marketplace}` : ''}`);
      }
    });
    if (opts.length === 0 && hasSelection) {
      opts.push(sellerLabel(currentAccount, currentProfile));
    }
    if (opts.length === 0) opts.push('Connect an Amazon account first');
    return opts;
  }, [accounts, currentAccount, currentProfile, hasSelection]);

  const planLabel = useMemo(() => {
    const plan = user?.plan || user?.subscription_plan || user?.billing_plan;
    if (!plan) return 'Monitor monthly';
    const s = String(plan).toLowerCase();
    if (s.includes('guard')) return 'Guard';
    if (s.includes('annual')) return 'Monitor annual';
    return 'Monitor monthly';
  }, [user]);

  const [screen, setScreen] = useState('booking');
  const [fName, setFName] = useState('');
  const [fEmail, setFEmail] = useState('');
  const [seller, setSeller] = useState('');
  const [agenda, setAgenda] = useState(AGENDA_OPTS[0]);
  const [notes, setNotes] = useState('');
  const [repPhase, setRepPhase] = useState(0);
  const [callStep, setCallStep] = useState(0);
  const [callSecs, setCallSecs] = useState(0);
  const [askText, setAskText] = useState('');
  const [qa, setQa] = useState([]);
  const [voiceOn, setVoiceOn] = useState(false);
  const [voiceMuted, setVoiceMuted] = useState(false);
  const [listening, setListening] = useState(false);
  const [thinking, setThinking] = useState(false);
  const [micError, setMicError] = useState('');
  const [sellerId, setSellerId] = useState(null);
  const [liveReport, setLiveReport] = useState(null);
  const [meetBusy, setMeetBusy] = useState(false);
  const [meetLink, setMeetLink] = useState('');
  const [meetRecallStatus, setMeetRecallStatus] = useState('');
  const [attendeeEmail, setAttendeeEmail] = useState('');
  const [voiceOptions, setVoiceOptions] = useState([]);
  const [selectedVoiceUri, setSelectedVoiceUri] = useState(() => {
    try {
      return localStorage.getItem(NORA_VOICE_KEY) || '';
    } catch {
      return '';
    }
  });
  const [voiceSupported] = useState(
    () => typeof window !== 'undefined' && typeof window.speechSynthesis !== 'undefined'
  );
  const [micSupported] = useState(() => Boolean(getSpeechRecognitionCtor()));
  const timers = useRef([]);
  const callTimer = useRef(null);
  const reportRef = useRef(null);
  const transcriptRef = useRef(null);
  const recognitionRef = useRef(null);
  const qaRef = useRef([]);
  const speakGenRef = useRef(0);
  const typeCancelRef = useRef(null);

  useEffect(() => {
    qaRef.current = qa;
  }, [qa]);

  const cancelNoraDelivery = useCallback(() => {
    speakGenRef.current += 1;
    if (typeCancelRef.current) {
      typeCancelRef.current();
      typeCancelRef.current = null;
    }
    stopNoraSpeech();
    setVoiceOn(false);
    setQa((prev) => prev.map((m) => (
      m.who === 'NORA' && m.typing
        ? { ...m, text: m.fullText || m.text, typing: false }
        : m
    )));
  }, []);

  useEffect(() => {
    if (user?.name) setFName(user.name);
    if (user?.email) setFEmail(user.email);
  }, [user?.name, user?.email]);

  useEffect(() => {
    if (sellerOptions[0]) setSeller((prev) => (prev && sellerOptions.includes(prev) ? prev : sellerOptions[0]));
  }, [sellerOptions]);

  useEffect(() => {
    let cancelled = false;
    async function loadSellerContext() {
      if (!accountId || !profileId) {
        setSellerId(null);
        setLiveReport(null);
        return;
      }
      try {
        const sellersRes = await sellersAPI.getAll({ account_id: accountId, profile_id: profileId });
        const sellers = Array.isArray(sellersRes.data) ? sellersRes.data : [];
        const id = sellers[0]?.id ?? null;
        if (cancelled) return;
        setSellerId(id);
        if (!id) {
          setLiveReport(null);
          return;
        }
        const reportRes = await sellersAPI.getAuditReport(id);
        if (!cancelled) setLiveReport(reportRes.data || null);
      } catch {
        if (!cancelled) {
          setSellerId(null);
          setLiveReport(null);
        }
      }
    }
    loadSellerContext();
    return () => { cancelled = true; };
  }, [accountId, profileId]);

  useEffect(() => () => {
    timers.current.forEach(clearTimeout);
    if (callTimer.current) clearInterval(callTimer.current);
    speakGenRef.current += 1;
    if (typeCancelRef.current) {
      typeCancelRef.current();
      typeCancelRef.current = null;
    }
    stopNoraSpeech();
    try {
      recognitionRef.current?.stop();
    } catch {
      /* ignore */
    }
  }, []);

  // Warm voice list (Chrome loads async).
  useEffect(() => {
    if (!voiceSupported) return undefined;
    const refresh = () => {
      const list = listNoraVoices();
      setVoiceOptions(list);
      setSelectedVoiceUri((prev) => {
        if (prev && list.some((v) => v.voiceURI === prev)) return prev;
        const auto = pickNoraVoice(prev);
        const next = auto?.voiceURI || '';
        try {
          if (next) localStorage.setItem(NORA_VOICE_KEY, next);
        } catch {
          /* ignore */
        }
        return next;
      });
    };
    refresh();
    window.speechSynthesis.addEventListener('voiceschanged', refresh);
    return () => window.speechSynthesis.removeEventListener('voiceschanged', refresh);
  }, [voiceSupported]);

  const brand = (seller || 'your account').split('·')[0].trim() || 'your account';

  const speakLine = useCallback((text) => {
    if (voiceMuted || !voiceSupported || !text) {
      setVoiceOn(false);
      return;
    }
    speakNoraLine(text, {
      voiceUri: selectedVoiceUri,
      onStart: () => setVoiceOn(true),
      onEnd: () => setVoiceOn(false),
    });
  }, [voiceMuted, voiceSupported, selectedVoiceUri]);

  const changeVoice = useCallback((uri) => {
    setSelectedVoiceUri(uri);
    try {
      localStorage.setItem(NORA_VOICE_KEY, uri || '');
    } catch {
      /* ignore */
    }
    // Short preview so the user hears the new voice immediately.
    if (!voiceMuted && uri) {
      cancelNoraDelivery();
      speakNoraLine('Hi, I\'m Nora. This is how I sound now.', {
        voiceUri: uri,
        onStart: () => setVoiceOn(true),
        onEnd: () => setVoiceOn(false),
      });
    }
  }, [voiceMuted, cancelNoraDelivery]);

  const scrollTranscript = useCallback(() => {
    requestAnimationFrame(() => {
      if (transcriptRef.current) transcriptRef.current.scrollTop = transcriptRef.current.scrollHeight;
    });
  }, []);

  const patchNoraReveal = useCallback((msgId, shown, fullText) => {
    setQa((prev) => prev.map((m) => (
      m.id === msgId
        ? { ...m, text: fullText.slice(0, shown) }
        : m
    )));
    scrollTranscript();
  }, [scrollTranscript]);

  /** Add Nora reply and reveal it as voice (or typewriter if muted). */
  const deliverNoraReply = useCallback((reply) => {
    const fullText = String(reply || '').trim();
    if (!fullText) return;

    cancelNoraDelivery();
    const gen = speakGenRef.current;
    const msgId = `nora-${Date.now()}-${gen}`;

    setQa((prev) => [...prev, {
      id: msgId,
      who: 'NORA',
      text: '',
      fullText,
      typing: true,
    }]);
    scrollTranscript();

    const alive = () => speakGenRef.current === gen;

    const finishMsg = () => {
      if (!alive()) return;
      typeCancelRef.current = null;
      setQa((prev) => prev.map((m) => (
        m.id === msgId
          ? { ...m, text: fullText, typing: false }
          : m
      )));
      setVoiceOn(false);
      scrollTranscript();
    };

    if (voiceMuted || !voiceSupported) {
      typeCancelRef.current = typeNoraLine(fullText, {
        onProgress: (n) => {
          if (!alive()) return;
          patchNoraReveal(msgId, n, fullText);
        },
        onEnd: finishMsg,
      });
      return;
    }

    speakNoraLine(fullText, {
      voiceUri: selectedVoiceUri,
      onStart: () => {
        if (alive()) setVoiceOn(true);
      },
      onProgress: (n) => {
        if (!alive()) return;
        patchNoraReveal(msgId, n, fullText);
      },
      onEnd: finishMsg,
    });
  }, [
    cancelNoraDelivery,
    voiceMuted,
    voiceSupported,
    selectedVoiceUri,
    patchNoraReveal,
    scrollTranscript,
  ]);

  const historyForApi = useCallback(() => (
    qaRef.current
      .filter((m) => m.who === 'YOU' || m.who === 'NORA')
      .map((m) => ({
        role: m.who === 'NORA' ? 'assistant' : 'user',
        content: m.fullText || m.text,
      }))
  ), []);

  const askWithText = useCallback(async (raw, { opening = false } = {}) => {
    const q = String(raw || '').trim();
    if (!q && !opening) return;
    if (thinking) return;

    cancelNoraDelivery();
    setThinking(true);
    setMicError('');

    if (!opening && q) {
      setQa((prev) => [...prev, { who: 'YOU', text: q }]);
      setAskText('');
      scrollTranscript();
    }

    try {
      const res = await noraAPI.chat({
        message: opening ? 'Start the session.' : q,
        opening: Boolean(opening),
        seller_id: sellerId || undefined,
        client_name: fName || user?.name || undefined,
        agenda: agenda || undefined,
        notes: notes || undefined,
        history: opening ? [] : historyForApi(),
      });
      const reply = res.data?.reply?.trim();
      if (!reply) throw new Error('Empty reply');
      deliverNoraReply(reply);
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Nora could not answer right now.';
      toast.error(msg);
      deliverNoraReply('I hit a connection issue answering that. Please try again in a moment.');
    } finally {
      setThinking(false);
    }
  }, [
    thinking,
    sellerId,
    fName,
    user?.name,
    agenda,
    notes,
    historyForApi,
    deliverNoraReply,
    cancelNoraDelivery,
    scrollTranscript,
  ]);

  const stopListening = useCallback(() => {
    try {
      recognitionRef.current?.stop();
    } catch {
      /* ignore */
    }
    recognitionRef.current = null;
    setListening(false);
  }, []);

  const startListening = useCallback(() => {
    const Ctor = getSpeechRecognitionCtor();
    if (!Ctor) {
      setMicError('Mic talk needs Chrome or Edge on HTTPS/localhost.');
      return;
    }
    if (thinking) return;
    setMicError('');
    cancelNoraDelivery();
    stopListening();

    const recognition = new Ctor();
    recognition.lang = 'en-US';
    recognition.interimResults = true;
    recognition.continuous = false;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => setListening(true);
    recognition.onerror = (event) => {
      setListening(false);
      recognitionRef.current = null;
      const code = event?.error || 'error';
      if (code === 'not-allowed') setMicError('Microphone permission blocked. Allow mic for this site.');
      else if (code === 'no-speech') setMicError('No speech heard — tap Mic and try again.');
      else if (code !== 'aborted') setMicError(`Mic error: ${code}`);
    };
    recognition.onend = () => {
      setListening(false);
      recognitionRef.current = null;
    };
    recognition.onresult = (event) => {
      let transcript = '';
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        transcript += event.results[i][0]?.transcript || '';
      }
      const finalText = transcript.trim();
      if (!finalText) return;
      setAskText(finalText);
      if (event.results[event.results.length - 1]?.isFinal) {
        askWithText(finalText);
      }
    };

    recognitionRef.current = recognition;
    try {
      recognition.start();
    } catch {
      setMicError('Could not start microphone. Try again.');
      setListening(false);
    }
  }, [askWithText, stopListening, thinking, cancelNoraDelivery]);

  const toggleMic = useCallback(() => {
    if (listening) stopListening();
    else startListening();
  }, [listening, startListening, stopListening]);

  const book = () => {
    setAttendeeEmail((prev) => prev.trim() || fEmail.trim() || user?.email || '');
    setScreen('confirm');
    setRepPhase(0);
    timers.current.forEach(clearTimeout);
    timers.current = [
      setTimeout(() => setRepPhase(1), 1200),
      setTimeout(() => setRepPhase(2), 2600),
    ];
  };

  const join = () => {
    setScreen('call');
    setCallStep(0);
    setCallSecs(0);
    setQa([]);
    setMicError('');
    cancelNoraDelivery();
    stopListening();
    if (callTimer.current) clearInterval(callTimer.current);
    callTimer.current = setInterval(() => setCallSecs((s) => s + 1), 1000);
    // Kick off live AI greeting — free-form discussion after this.
    setTimeout(() => askWithText('Start the session.', { opening: true }), 200);
  };

  const endCall = () => {
    stopListening();
    cancelNoraDelivery();
    setThinking(false);
    if (callTimer.current) clearInterval(callTimer.current);
    setScreen('confirm');
  };

  const ask = () => askWithText(askText);

  const startGoogleMeet = useCallback(async () => {
    if (meetBusy) return;
    setMeetBusy(true);
    try {
      const inviteEmail = (attendeeEmail || fEmail || user?.email || '').trim();
      const res = await meetingAgentAPI.createInstant({
        client_name: fName || user?.name || undefined,
        client_email: inviteEmail || undefined,
        agenda: agenda || undefined,
        notes: notes || undefined,
        seller_id: sellerId || undefined,
        title: `Nora · ${(fName || brand || 'Client').trim()}`,
      });
      const link = res.data?.meet_link;
      if (!link) throw new Error(res.data?.message || 'No Meet link returned');
      setMeetLink(link);
      setMeetRecallStatus(res.data?.meeting?.recall_status || (res.data?.recall_error ? 'error' : 'joining'));
      if (res.data?.recall_error) {
        toast.warning(res.data.recall_error);
      } else if (res.data?.nora_can_speak === false) {
        toast.warning(res.data?.message || 'Nora joined but speaking is not active yet.');
      } else if (res.data?.nora_auto_admit === false) {
        toast.message(res.data?.message || 'Admit Nora from host Gmail — then she can speak.');
      } else {
        toast.success(res.data?.message || 'Google Meet ready — Nora is joining to discuss your audit.');
      }
      window.open(link, '_blank', 'noopener,noreferrer');
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Could not create Google Meet.');
    } finally {
      setMeetBusy(false);
    }
  }, [meetBusy, fName, fEmail, attendeeEmail, user?.name, user?.email, agenda, notes, sellerId, brand]);

  const speaking = screen === 'call' && voiceOn;
  const statusLabel = !voiceSupported
    ? 'Voice unavailable in this browser'
    : voiceMuted
      ? 'Muted'
      : thinking
        ? 'Thinking…'
        : listening
          ? 'Listening to you…'
          : speaking
            ? 'Speaking'
            : 'Ready — ask anything';
  const mm = String(Math.floor(callSecs / 60)).padStart(2, '0');
  const ss = String(callSecs % 60).padStart(2, '0');
  const curSec = '';
  const hl = (k) => ({
    padding: '28px 32px',
    borderLeft: `3px solid ${curSec === k ? '#0B7A8F' : 'transparent'}`,
    background: curSec === k ? '#F2F8F9' : 'transparent',
    transition: 'background .3s, border-color .3s',
  });

  const confirmRows = [
    { k: 'MODE', v: 'In-app chat or Google Meet with Nora' },
    { k: 'WHERE', v: 'Protego · this screen, or Google Meet' },
    { k: 'WITH', v: 'Nora · Protego AI agent' },
    { k: 'AGENDA', v: agenda },
    { k: 'WHO', v: `${fName || '—'} · ${fEmail || '—'}` },
  ];

  const tItems = qa.map((m, i) => ({
    who: m.who,
    text: m.text,
    typing: Boolean(m.typing),
    active: i === qa.length - 1 && m.who === 'NORA' && (speaking || thinking || m.typing),
  }));

  const exec = liveReport?.executive_summary || {};
  const score = exec.enforcement_risk_score ?? 87;
  const projected = exec.projected_score ?? 82;
  const riskLabel = exec.risk_label || 'Low risk';
  const metricsMonitored = exec.metrics_monitored ?? 28;
  const metricsBreach = exec.metrics_in_breach ?? 5;
  const criticalAlerts = exec.critical_alerts ?? 3;
  const flaggedAsins = exec.flagged_asins ?? 68;
  const coverHeadline = liveReport?.cover?.headline
    || `Account Health reads managed.\n${metricsBreach} signals are in active breach.`;
  const drivers = (liveReport?.top_drivers || []).slice(0, 6).map((d) => [
    d.signal_name,
    d.severity || 'Info',
    d.current_value || '—',
    d.baseline || '—',
    `−${Math.abs(Number(d.score_contribution || 0)).toFixed(2)}`,
  ]);
  const fallbackDrivers = [
    ['Cancellation Rate', 'Critical', '13.71%', '1.8%', '−4.91'],
    ['Negative Feedback Rate', 'Critical', '45 of 45', '5%', '−3.00'],
    ['Return Dissatisfaction', 'Warning', '24.74%', '10%', '−2.42'],
    ['Shipping Cost Spike', 'Warning', '158.52%', '25%', '−1.15'],
    ['Refund Processing Time', 'Warning', '183.89 hrs', '48 hrs', '−1.00'],
    ['Weekend Shipment Failure', 'Warning', '68.82%', '4%', '−0.81'],
  ];
  const driverRows = drivers.length ? drivers : fallbackDrivers;
  const crit = '#BE123C';
  const warn = '#7E22CE';

  return (
    <div className="nora-root" data-testid="book-nora-page">
      <div
        style={{
          height: 64,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 24px',
          borderBottom: '1px solid var(--border-subtle)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontFamily: 'var(--font-data)', fontSize: 11, letterSpacing: '.14em', color: 'var(--text-tertiary)' }}>
            NORA
          </span>
        </div>
        {screen === 'call' ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <LiveDot />
              <span style={{ fontFamily: 'var(--font-data)', fontSize: 11, letterSpacing: '.08em', color: 'var(--text-secondary)' }}>
                LIVE · NORA SESSION
              </span>
            </div>
            <span style={{ fontFamily: 'var(--font-data)', fontSize: 12 }}>{mm}:{ss}</span>
            <span
              style={{
                fontFamily: 'var(--font-data)',
                fontSize: 11,
                color: 'var(--text-secondary)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 999,
                padding: '5px 12px',
              }}
            >
              Plan · {planLabel}
            </span>
            <NoraButton
              variant="ghost"
              size="sm"
              onClick={endCall}
              style={{ border: '1px solid var(--border-subtle)', color: 'var(--text-secondary)' }}
              data-testid="nora-end-meeting-header"
            >
              End meeting
            </NoraButton>
          </div>
        ) : (
          <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>Nora · Protego agent</span>
        )}
      </div>

      {screen === 'booking' && (
        <div style={{ maxWidth: 560, margin: '0 auto', padding: '64px 24px 96px' }}>
          <div style={{ fontFamily: 'var(--font-data)', fontSize: 11, letterSpacing: '.18em', color: 'var(--text-tertiary)', marginBottom: 16 }}>
            TALK TO NORA
          </div>
          <h1 style={{ fontSize: 40, lineHeight: 1.1, margin: '0 0 12px' }}>Talk to Nora.</h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: 15, lineHeight: 1.6, maxWidth: '56ch', margin: '0 0 40px' }}>
            Nora reads your account with you — audit reports, signals, Seller Central setup, and plan questions. No calendar booking needed; start a session in the app.
          </p>
          <div
            style={{
              background: 'var(--surface-1)',
              border: '1px solid var(--border-subtle)',
              borderRadius: 16,
              padding: 24,
              minHeight: 560,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 4 }}>
              <div style={{ fontSize: 16, fontWeight: 500 }}>Start a Nora session</div>
              <StatusBadge tone="info">In-app agent</StatusBadge>
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 20 }}>Live in-app walkthrough</div>
            <div style={{ fontSize: 14, color: 'var(--text-secondary)', margin: '0 0 8px' }}>Nora can help with</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, margin: '0 0 24px' }}>
              {AGENDA_OPTS.map((o) => (
                <div key={o} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, color: 'var(--text-secondary)' }}>
                  <span style={{ width: 12, height: 1, background: 'var(--cyan)', flex: 'none' }} />
                  {o}
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid var(--border-subtle)', paddingTop: 20, marginTop: 'auto' }}>
              <NoraButton variant="primary" size="md" onClick={() => setScreen('details')}>Start session</NoraButton>
            </div>
          </div>
        </div>
      )}

      {screen === 'details' && (
        <div style={{ maxWidth: 560, margin: '0 auto', padding: '64px 24px 96px' }}>
          <div style={{ fontFamily: 'var(--font-data)', fontSize: 11, letterSpacing: '.18em', color: 'var(--text-tertiary)', marginBottom: 16 }}>
            YOUR DETAILS
          </div>
          <h1 style={{ fontSize: 28, margin: '0 0 24px' }}>Tell Nora who&apos;s joining.</h1>
          <div
            style={{
              background: 'var(--surface-1)',
              border: '1px solid var(--border-subtle)',
              borderRadius: 16,
              padding: 24,
              minHeight: 560,
              display: 'flex',
              flexDirection: 'column',
              gap: 18,
              overflow: 'auto',
            }}
          >
            <div>
              <div style={fieldLabel()}>Your name</div>
              <input value={fName} onChange={(e) => setFName(e.target.value)} style={inputStyle()} />
            </div>
            <div>
              <div style={fieldLabel()}>Email</div>
              <input type="email" value={fEmail} onChange={(e) => setFEmail(e.target.value)} style={inputStyle()} />
              <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 6 }}>
                Pulled from your Protego account.
              </div>
            </div>
            <div>
              <div style={fieldLabel()}>Amazon seller account</div>
              <div style={{ position: 'relative' }}>
                <select value={seller} onChange={(e) => setSeller(e.target.value)} style={selectStyle()}>
                  {sellerOptions.map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
                <span style={{ position: 'absolute', right: 14, top: 12, color: 'var(--text-tertiary)', pointerEvents: 'none', fontSize: 12 }}>▾</span>
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 6 }}>
                Connected via SP-API. Nora uses this account for the walkthrough.
              </div>
            </div>
            <div>
              <div style={fieldLabel()}>Agenda</div>
              <div style={{ position: 'relative' }}>
                <select value={agenda} onChange={(e) => setAgenda(e.target.value)} style={selectStyle()}>
                  {AGENDA_OPTS.map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
                <span style={{ position: 'absolute', right: 14, top: 12, color: 'var(--text-tertiary)', pointerEvents: 'none', fontSize: 12 }}>▾</span>
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 6 }}>{AGENDA_HELP[agenda]}</div>
            </div>
            <div>
              <div style={fieldLabel()}>Anything Nora should prepare?</div>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional — questions, specific ASINs, context."
                style={{
                  width: '100%',
                  minHeight: 80,
                  background: 'var(--surface-2)',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: 10,
                  color: 'var(--text-primary)',
                  fontFamily: 'var(--font-body)',
                  fontSize: 14,
                  padding: '12px 14px',
                  resize: 'vertical',
                }}
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, borderTop: '1px solid var(--border-subtle)', paddingTop: 20, marginTop: 'auto' }}>
              <NoraButton variant="ghost" size="md" onClick={() => setScreen('booking')}>Back</NoraButton>
              <NoraButton variant="primary" size="md" onClick={book} disabled={!fName.trim() || !fEmail.trim()}>
                Continue
              </NoraButton>
            </div>
          </div>
        </div>
      )}

      {screen === 'confirm' && (
        <div style={{ maxWidth: 640, margin: '0 auto', padding: '64px 24px 96px' }}>
          <div style={{ fontFamily: 'var(--font-data)', fontSize: 11, letterSpacing: '.18em', color: 'var(--text-tertiary)', marginBottom: 16 }}>
            READY
          </div>
          <h1 style={{ fontSize: 28, margin: '0 0 24px' }}>Session ready.</h1>
          <div style={{ background: 'var(--surface-1)', border: '1px solid var(--border-subtle)', borderRadius: 16, padding: 24, marginBottom: 16 }}>
            {confirmRows.map((r) => (
              <div key={r.k} style={{ display: 'flex', gap: 16, padding: '9px 0', borderBottom: '1px solid var(--border-subtle)', fontSize: 14 }}>
                <span style={{ width: 96, flex: 'none', color: 'var(--text-tertiary)', fontSize: 12, fontFamily: 'var(--font-data)', letterSpacing: '.08em', paddingTop: 2 }}>{r.k}</span>
                <span>{r.v}</span>
              </div>
            ))}
          </div>
          <div style={{ background: 'var(--surface-1)', border: '1px solid var(--border-subtle)', borderRadius: 16, padding: 24, marginBottom: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 16 }}>
              <div style={{ fontSize: 15, fontWeight: 500 }}>Your account health audit</div>
              <StatusBadge tone={repPhase === 2 ? 'success' : 'info'}>{repPhase === 2 ? 'Generated' : 'Generating'}</StatusBadge>
            </div>
            {repPhase < 2 ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--text-secondary)', fontSize: 13 }}>
                <LiveDot />
                <span>{repPhase === 0 ? 'Syncing Selling Partner API…' : 'Reading 28 metrics across 6 domains…'}</span>
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, background: 'var(--surface-2)', border: '1px solid var(--border-subtle)', borderRadius: 10, padding: '14px 16px' }}>
                  <span style={{ flex: 'none', width: 40, height: 44, border: '1px solid var(--border-strong)', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-data)', fontSize: 10, color: 'var(--cyan)' }}>PDF</span>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 500 }}>Account health audit — {brand}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 2 }}>11 pages · 28 metrics · generated just now</div>
                  </div>
                  <NoraButton variant="ghost" size="sm">Download</NoraButton>
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 12 }}>
                  Nora will walk this report with you in the session.
                </div>
              </>
            )}
          </div>
          <div style={{ background: 'var(--surface-1)', border: '1px solid var(--border-subtle)', borderRadius: 16, padding: 20, marginBottom: 16 }}>
            <div style={fieldLabel()}>Attendee email (Meet invite)</div>
            <input
              type="email"
              value={attendeeEmail}
              onChange={(e) => setAttendeeEmail(e.target.value)}
              placeholder="you@company.com"
              data-testid="nora-attendee-email"
              style={inputStyle()}
            />
            <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 6 }}>
              Google invite goes to this address — join Meet with the same account.
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
            <NoraButton
              variant="secondary"
              size="lg"
              disabled={repPhase < 2 || meetBusy || !attendeeEmail.trim()}
              onClick={startGoogleMeet}
              data-testid="nora-join-google-meet"
            >
              {meetBusy ? 'Creating Meet…' : 'Join Nora on Google Meet'}
            </NoraButton>
            <NoraButton variant="primary" size="lg" disabled={repPhase < 2} onClick={join}>
              Start in-app session
            </NoraButton>
          </div>
          {meetLink ? (
            <div style={{ marginTop: 12, fontSize: 12, color: 'var(--text-secondary)', textAlign: 'right' }}>
              Meet ready
              {meetRecallStatus ? ` · Nora: ${meetRecallStatus}` : ''}.{' '}
              <a href={meetLink} target="_blank" rel="noreferrer" style={{ color: 'var(--cyan)' }}>
                Open link again
              </a>
            </div>
          ) : null}
        </div>
      )}

      {screen === 'call' && (
        <div
          className="nora-call-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0,1fr) 400px',
            gap: 20,
            padding: '20px 24px',
            height: 'calc(100vh - 64px)',
          }}
        >
          <div ref={reportRef} id="report-scroll" className="nora-report-scroll" style={{ background: 'var(--doc-bg)', borderRadius: 16, overflow: 'auto', color: 'var(--doc-text)', minWidth: 0 }}>
            <div id="sec-cover" style={hl('cover')}>
              <div style={{ fontFamily: 'var(--font-data)', fontSize: 10, letterSpacing: '.3em', color: '#0B7A8F', marginBottom: 14 }}>ENFORCEMENT RISK DIAGNOSTIC</div>
              <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 30, lineHeight: 1.15, letterSpacing: '-.01em', marginBottom: 14, whiteSpace: 'pre-line' }}>
                {coverHeadline}
              </div>
              <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', fontSize: 12, color: '#55555F' }}>
                <span>Prepared for <strong style={{ color: '#0A0A0F' }}>{brand}</strong></span>
                <span>{seller.includes('·') ? seller.split('·')[1]?.trim() : 'amazon.com (US)'}</span>
                <span style={{ fontFamily: 'var(--font-data)' }}>{new Date().toLocaleString()}</span>
                <span>Live SP-API sync</span>
              </div>
            </div>

            <div id="sec-sum" style={hl('sum')}>
              <div style={{ fontFamily: 'var(--font-data)', fontSize: 10, letterSpacing: '.24em', color: '#55555F', marginBottom: 10 }}>01 · EXECUTIVE SUMMARY</div>
              <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 20, marginBottom: 16 }}>The surface is holding. The leading indicators are not.</div>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 24, marginBottom: 20, flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontFamily: 'var(--font-data)', fontSize: 48, fontWeight: 500, lineHeight: 1 }}>{score}</div>
                  <div style={{ fontSize: 12, color: '#55555F' }}>{riskLabel} projected</div>
                </div>
                <div style={{ fontSize: 14, color: '#55555F', paddingBottom: 8 }}>{projected} if issues are not resolved</div>
                <div style={{ marginLeft: 'auto', fontSize: 12, color: '#55555F' }}>0 critical · 100 healthy</div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0,1fr))', gap: 12 }}>
                {[
                  [String(metricsMonitored), 'metrics monitored across 6 domains'],
                  [String(metricsBreach), 'metrics in active breach'],
                  [String(criticalAlerts), 'critical-severity alerts'],
                  [String(flaggedAsins), 'ASINs carrying a risk flag'],
                ].map(([v, l]) => (
                  <div key={l} style={{ background: '#F7F7F9', borderRadius: 10, padding: 12 }}>
                    <div style={{ fontFamily: 'var(--font-data)', fontSize: 20 }}>{v}</div>
                    <div style={{ fontSize: 11, color: '#55555F', marginTop: 4 }}>{l}</div>
                  </div>
                ))}
              </div>
            </div>

            <div id="sec-gap" style={hl('gap')}>
              <div style={{ fontFamily: 'var(--font-data)', fontSize: 10, letterSpacing: '.24em', color: '#55555F', marginBottom: 10 }}>02 · THE GAP</div>
              <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 20, marginBottom: 16 }}>What Amazon shows you vs. what we see underneath.</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div style={{ background: '#F7F7F9', borderRadius: 12, padding: 16 }}>
                  <div style={{ fontSize: 11, letterSpacing: '.12em', color: '#55555F', marginBottom: 10 }}>AMAZON DISPLAYS TODAY</div>
                  {[
                    ['Order Defect Rate', '2.28% in range'],
                    ['Valid Tracking Rate', '99.68%'],
                    ['A-to-Z Claim Rate', '0.06% safe'],
                    ['Open Policy Violations', '0'],
                  ].map(([k, v]) => (
                    <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '6px 0', borderBottom: '1px solid #E3E3E8' }}>
                      <span>{k}</span><span style={{ fontFamily: 'var(--font-data)' }}>{v}</span>
                    </div>
                  ))}
                  <div style={{ fontSize: 12, color: '#0B7A8F', marginTop: 10 }}>Reads as managed</div>
                </div>
                <div style={{ background: '#F7F7F9', borderRadius: 12, padding: 16 }}>
                  <div style={{ fontSize: 11, letterSpacing: '.12em', color: '#55555F', marginBottom: 10 }}>PROTEGO READS BENEATH IT</div>
                  {[
                    ['Cancellation Rate', '18.06% · 10×', crit],
                    ['Negative Feedback', '100% · 50 of 50', crit],
                    ['Refund Processing', '183.9 hrs · 3.8×', warn],
                    ['Weekend Failure', '68.82% · 17×', warn],
                  ].map(([k, v, c]) => (
                    <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '6px 0', borderBottom: '1px solid #E3E3E8' }}>
                      <span>{k}</span><span style={{ fontFamily: 'var(--font-data)', color: c }}>{v}</span>
                    </div>
                  ))}
                  <div style={{ fontSize: 12, color: crit, marginTop: 10 }}>2 critical · 3 warnings, building</div>
                </div>
              </div>
            </div>

            <div id="sec-traj" style={hl('traj')}>
              <div style={{ fontFamily: 'var(--font-data)', fontSize: 10, letterSpacing: '.24em', color: '#55555F', marginBottom: 10 }}>03 · SCORE AND TRAJECTORY</div>
              <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 20, marginBottom: 12 }}>The score reflects velocity, not just thresholds.</div>
              <div style={{ display: 'flex', gap: 20, alignItems: 'baseline', marginBottom: 8 }}>
                <span style={{ fontFamily: 'var(--font-data)', fontSize: 28 }}>94.91</span>
                <span style={{ fontFamily: 'var(--font-data)', fontSize: 28 }}>87</span>
                <span style={{ fontFamily: 'var(--font-data)', fontSize: 28, color: '#55555F' }}>82</span>
              </div>
              <p style={{ fontSize: 14, color: '#55555F', margin: 0 }}>
                Over the trailing window the score has fallen from 94.91 as buyer-trust signals compounded. Solid is measured; dashed is projected.
              </p>
            </div>

            <div id="sec-drv" style={hl('drv')}>
              <div style={{ fontFamily: 'var(--font-data)', fontSize: 10, letterSpacing: '.24em', color: '#55555F', marginBottom: 10 }}>04 · TOP RISK DRIVERS</div>
              <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 20, marginBottom: 16 }}>Six signals are actively contributing to your score.</div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ textAlign: 'left', fontSize: 11, color: '#55555F' }}>
                    <th style={{ padding: '8px 8px 8px 0', fontWeight: 500 }}>SIGNAL</th>
                    <th style={{ padding: 8, fontWeight: 500 }}>SEVERITY</th>
                    <th style={{ padding: 8, fontWeight: 500 }}>CURRENT</th>
                    <th style={{ padding: 8, fontWeight: 500 }}>BASELINE</th>
                    <th style={{ padding: 8, fontWeight: 500, textAlign: 'right' }}>IMPACT</th>
                  </tr>
                </thead>
                <tbody>
                  {driverRows.map((r) => (
                    <tr key={r[0]}>
                      <td style={{ padding: '9px 8px 9px 0', borderBottom: '1px solid #E3E3E8', fontSize: 12, fontWeight: 500 }}>{r[0]}</td>
                      <td style={{ padding: 8, borderBottom: '1px solid #E3E3E8', fontSize: 12, color: r[1] === 'Critical' ? crit : warn, fontWeight: 500 }}>{r[1]}</td>
                      <td style={{ padding: 8, borderBottom: '1px solid #E3E3E8', fontSize: 12, fontFamily: 'var(--font-data)' }}>{r[2]}</td>
                      <td style={{ padding: 8, borderBottom: '1px solid #E3E3E8', fontSize: 12, fontFamily: 'var(--font-data)', color: '#55555F' }}>{r[3]}</td>
                      <td style={{ padding: 8, borderBottom: '1px solid #E3E3E8', fontSize: 12, fontFamily: 'var(--font-data)', textAlign: 'right' }}>{r[4]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div id="sec-cx" style={hl('cx')}>
              <div style={{ fontFamily: 'var(--font-data)', fontSize: 10, letterSpacing: '.24em', color: '#55555F', marginBottom: 10 }}>05 · CUSTOMER EXPERIENCE · DEEP DIVE</div>
              <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 20, marginBottom: 16 }}>This is where the account is bleeding.</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                {[
                  ['18.06%', 'target 1.8%', 'Cancellation Rate', crit],
                  ['100%', 'target 5%', 'Negative Feedback Rate', crit],
                  ['183.9 hrs', 'target 48 hrs', 'Refund Processing Time', warn],
                  ['24.74%', 'target 10%', 'Return Dissatisfaction Rate', warn],
                ].map(([v, t, n, c]) => (
                  <div key={n} style={{ border: '1px solid #E3E3E8', borderRadius: 12, padding: 14 }}>
                    <div style={{ fontFamily: 'var(--font-data)', fontSize: 24, color: c }}>{v}</div>
                    <div style={{ fontSize: 11, color: '#55555F' }}>{t}</div>
                    <div style={{ fontSize: 13, fontWeight: 500, marginTop: 6 }}>{n}</div>
                  </div>
                ))}
              </div>
            </div>

            <div id="sec-ship" style={hl('ship')}>
              <div style={{ fontFamily: 'var(--font-data)', fontSize: 10, letterSpacing: '.24em', color: '#55555F', marginBottom: 10 }}>06 · SHIPPING AND FULFILLMENT · DEEP DIVE</div>
              <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 20, marginBottom: 16 }}>Late Shipments sit on the line.</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                {[
                  ['4.0%', 'threshold 4%', 'Late Shipment Rate'],
                  ['68.82%', 'target 4%', 'Weekend Shipment Failure'],
                  ['158.5%', 'baseline 25%', 'Shipping Cost Spike'],
                  ['0.64%', 'target 3%', 'Delivery Promise Breach'],
                ].map(([v, t, n]) => (
                  <div key={n} style={{ border: '1px solid #E3E3E8', borderRadius: 12, padding: 14 }}>
                    <div style={{ fontFamily: 'var(--font-data)', fontSize: 24 }}>{v}</div>
                    <div style={{ fontSize: 11, color: '#55555F' }}>{t}</div>
                    <div style={{ fontSize: 13, fontWeight: 500, marginTop: 6 }}>{n}</div>
                  </div>
                ))}
              </div>
            </div>

            <div id="sec-asin" style={hl('asin')}>
              <div style={{ fontFamily: 'var(--font-data)', fontSize: 10, letterSpacing: '.24em', color: '#55555F', marginBottom: 10 }}>07 · ASIN CONCENTRATION</div>
              <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 20, marginBottom: 16 }}>Risk is concentrated in a few SKUs.</div>
              {[
                ['B00K1JFAVE', '59'],
                ['B07D6WZ76C', '59'],
                ['B08KH7873B', '59'],
                ['B0BBPNPZTC', '49'],
                ['B0DK9ZJM7V', '49'],
              ].map(([asin, score]) => (
                <div key={asin} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #E3E3E8', fontSize: 13 }}>
                  <span style={{ fontFamily: 'var(--font-data)' }}>{asin}</span>
                  <span style={{ color: warn, fontFamily: 'var(--font-data)' }}>{score}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="nora-call-side" style={{ display: 'flex', flexDirection: 'column', gap: 12, minHeight: 0 }}>
            <div style={{ background: 'var(--surface-1)', border: '1px solid var(--border-subtle)', borderRadius: 16, padding: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <div style={{ width: 36, height: 36, borderRadius: 999, background: 'var(--surface-2)', border: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-data)', fontSize: 10, color: 'var(--cyan)' }}>N</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 500 }}>Nora</div>
                  <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
                    {statusLabel}
                  </div>
                </div>
                <NoraWave speaking={speaking || listening} />
              </div>
              <div style={{ marginBottom: 10 }}>
                <div style={fieldLabel({ color: 'var(--text-secondary)', marginBottom: 6 })}>Nora voice</div>
                <select
                  value={selectedVoiceUri}
                  disabled={!voiceSupported || voiceOptions.length === 0}
                  onChange={(e) => changeVoice(e.target.value)}
                  style={{
                    ...selectStyle(),
                    height: 36,
                    fontSize: 13,
                    background: 'var(--surface-2)',
                  }}
                  data-testid="nora-voice-select"
                >
                  {voiceOptions.length === 0 && <option value="">Loading voices…</option>}
                  {voiceOptions.map((v) => (
                    <option key={v.voiceURI} value={v.voiceURI}>
                      {v.name} ({v.lang})
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <NoraButton
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    const next = !voiceMuted;
                    setVoiceMuted(next);
                    if (next) {
                      cancelNoraDelivery();
                    } else if (screen === 'call') {
                      const last = [...qa].reverse().find((m) => m.who === 'NORA');
                      const text = last?.fullText || last?.text;
                      if (text) speakLine(text);
                    }
                  }}
                  style={{ flex: 1, border: '1px solid var(--border-subtle)' }}
                >
                  {voiceMuted ? 'Unmute Nora' : 'Mute'}
                </NoraButton>
                <NoraButton
                  variant="secondary"
                  size="sm"
                  disabled={thinking}
                  onClick={() => askWithText('Walk me through the biggest risks on this audit and what I should do first.')}
                  style={{ flex: 1 }}
                >
                  Guide me
                </NoraButton>
              </div>
              <NoraButton
                variant="secondary"
                size="sm"
                onClick={endCall}
                style={{
                  width: '100%',
                  marginTop: 8,
                  borderColor: 'rgba(244,63,94,.35)',
                  color: 'var(--text-secondary)',
                }}
                data-testid="nora-end-meeting"
                title="End this Nora meeting and return to the summary"
              >
                End meeting
              </NoraButton>
              <div style={{ marginTop: 8 }}>
                <div style={{ ...fieldLabel(), marginBottom: 4 }}>Attendee email</div>
                <input
                  type="email"
                  value={attendeeEmail}
                  onChange={(e) => setAttendeeEmail(e.target.value)}
                  placeholder="you@company.com"
                  data-testid="nora-call-attendee-email"
                  style={{ ...inputStyle(), fontSize: 13, padding: '8px 10px' }}
                />
              </div>
              <NoraButton
                variant="secondary"
                size="sm"
                disabled={meetBusy || !attendeeEmail.trim()}
                onClick={startGoogleMeet}
                style={{ width: '100%', marginTop: 8 }}
                data-testid="nora-call-google-meet"
              >
                {meetBusy ? 'Creating Meet…' : meetLink ? 'Open Google Meet again' : 'Join Nora on Google Meet'}
              </NoraButton>
              {meetLink ? (
                <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 6, wordBreak: 'break-all' }}>
                  {meetRecallStatus ? `Nora bot: ${meetRecallStatus}. ` : null}
                  <a href={meetLink} target="_blank" rel="noreferrer" style={{ color: 'var(--cyan)' }}>
                    {meetLink}
                  </a>
                </div>
              ) : null}
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 8 }}>
                Live 2-way talk — ask anything by mic or text. Nora uses your account audit context.
                {sellerId ? '' : ' Select an Amazon account for live numbers.'}
              </div>
              {(!voiceSupported || !micSupported || micError) && (
                <div style={{ fontSize: 11, color: micError ? 'var(--warning)' : 'var(--text-tertiary)', marginTop: 4 }}>
                  {micError
                    || (!micSupported
                      ? 'Mic talk needs Chrome/Edge. You can still type below.'
                      : !voiceSupported
                        ? 'Use Chrome/Edge/Safari for Nora voice (browser speech).'
                        : null)}
                </div>
              )}
            </div>

            <div
              ref={transcriptRef}
              id="transcript-scroll"
              className="nora-transcript-scroll"
              style={{ flex: 1, minHeight: 0, overflow: 'auto', background: 'var(--surface-1)', border: '1px solid var(--border-subtle)', borderRadius: 16, padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}
            >
              {tItems.map((m, i) => (
                <div
                  key={`${m.who}-${i}`}
                  style={{
                    padding: '10px 12px',
                    borderRadius: 10,
                    background: 'var(--surface-2)',
                    border: m.active ? '1px solid rgba(0,212,255,.4)' : '1px solid var(--border-subtle)',
                  }}
                >
                  <div style={{ fontFamily: 'var(--font-data)', fontSize: 10, letterSpacing: '.08em', color: m.who === 'YOU' ? 'var(--text-secondary)' : 'var(--cyan)', marginBottom: 4 }}>{m.who}</div>
                  <div style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--text-primary)', whiteSpace: 'pre-wrap' }}>
                    {m.text}
                    {m.typing ? (
                      <span
                        aria-hidden
                        style={{
                          display: 'inline-block',
                          width: 7,
                          marginLeft: 2,
                          borderRadius: 1,
                          background: 'var(--cyan)',
                          animation: 'nora-caret 1s steps(1) infinite',
                          verticalAlign: 'text-bottom',
                          height: '1em',
                        }}
                      />
                    ) : null}
                  </div>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <NoraButton
                variant={listening ? 'primary' : 'secondary'}
                size="md"
                onClick={toggleMic}
                disabled={!micSupported}
                style={{
                  minWidth: 52,
                  padding: '0 14px',
                  borderColor: listening ? 'var(--cyan)' : undefined,
                }}
                title={listening ? 'Stop listening' : 'Talk to Nora'}
              >
                {listening ? 'Stop' : 'Mic'}
              </NoraButton>
              <input
                value={askText}
                onChange={(e) => setAskText(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !thinking) ask(); }}
                placeholder={listening ? 'Listening… speak now' : thinking ? 'Nora is thinking…' : 'Ask Nora anything…'}
                disabled={thinking}
                style={{ ...inputStyle(), flex: 1, opacity: thinking ? 0.7 : 1 }}
              />
              <NoraButton variant="primary" size="md" onClick={ask} disabled={thinking || !askText.trim()}>
                {thinking ? '…' : 'Ask'}
              </NoraButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
